/**
 * SpecsBlock Component - 規格展示區塊
 * Requirements: 7.2, 7.3
 */

import {
  CpuChipIcon,
  CircleStackIcon,
  ServerIcon,
  SignalIcon,
  BoltIcon,
  GlobeAltIcon,
  BuildingOfficeIcon,
  UserIcon,
  CubeIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import type { CardData, CategoryType, DiskConfig } from '../../types/card';
import type { Translations } from '../../i18n';

interface SpecsBlockProps {
  cardData: CardData;
  t: Translations;
}

/** 判斷是否為伺服器類型（VPS/VDS 需要線路，虛擬主機不需要） */
function needsRouteType(category: CategoryType): boolean {
  return category === 'vps' || category === 'vds';
}

/** 判斷是否為伺服器類型 */
function isServerType(category: CategoryType): boolean {
  return ['vps', 'vds', 'hosting'].includes(category);
}

/** 格式化硬碟配置 */
function formatDisk(disk: DiskConfig): string {
  return `${disk.size}${disk.unit} ${disk.type}`;
}

/** 格式化流量 */
function formatTraffic(cardData: CardData, t: Translations): string {
  if (cardData.traffic.isUnlimited) {
    return `∞ ${t.unlimitedTraffic}`;
  }
  return `${cardData.traffic.value}${cardData.traffic.unit}`;
}

/** 格式化流量方向 */
function formatDirection(direction: string, t: Translations): string {
  const directionMap: Record<string, string> = {
    '雙向計算': t.directionBoth,
    '只算入站': t.directionIn,
    '只算出站': t.directionOut,
  };
  return directionMap[direction] || direction;
}

/** 規格卡片組件 - 全寬 */
function SpecCardFull({ icon: Icon, label, value, subValue }: { 
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>; 
  label: string; 
  value: string;
  subValue?: string;
}) {
  return (
    <div className="bg-slate-100 rounded-lg p-3 flex items-start gap-3">
      <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-blue-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
        <p className="font-semibold text-slate-800">{value}</p>
        {subValue && (
          <p className="text-xs text-slate-500">{subValue}</p>
        )}
      </div>
    </div>
  );
}

/** 規格卡片組件 - 半寬 */
function SpecCardHalf({ icon: Icon, label, value, subValue }: { 
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>; 
  label: string; 
  value: string;
  subValue?: string;
}) {
  return (
    <div className="bg-slate-100 rounded-lg p-3 flex items-start gap-2">
      <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-blue-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
        <p className="font-semibold text-slate-800 text-sm truncate">{value}</p>
        {subValue && (
          <p className="text-xs text-slate-500">{subValue}</p>
        )}
      </div>
    </div>
  );
}

/** 伺服器規格區塊 */
function ServerSpecs({ cardData, t }: { cardData: CardData; t: Translations }) {
  const { cpu, ram, disks, traffic } = cardData;

  return (
    <div className="p-4 space-y-3">
      <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">{t.specsConfig}</h3>
      
      {/* CPU 和 RAM - 並排 */}
      <div className="grid grid-cols-2 gap-2">
        <SpecCardHalf icon={CpuChipIcon} label={t.cpu} value={cpu} />
        <SpecCardHalf icon={CircleStackIcon} label={t.ram} value={ram} />
      </div>
      
      {/* 硬碟 - 每顆獨立一行 */}
      <div className="space-y-2">
        {disks.map((disk, index) => (
          <SpecCardFull 
            key={index}
            icon={ServerIcon} 
            label={`${t.disk} ${disks.length > 1 ? index + 1 : ''}`}
            value={formatDisk(disk)} 
          />
        ))}
      </div>
      
      {/* 流量和端口 - 並排 */}
      <div className="grid grid-cols-2 gap-2">
        <SpecCardHalf 
          icon={SignalIcon} 
          label={t.traffic} 
          value={formatTraffic(cardData, t)}
          subValue={!traffic.isUnlimited ? formatDirection(traffic.direction, t) : undefined}
        />
        <SpecCardHalf 
          icon={BoltIcon} 
          label={t.portSpeed} 
          value={`${traffic.speed}${traffic.speedUnit}`} 
        />
      </div>

      {/* 限速警告 */}
      {traffic.throttleInfo && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
          <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <p className="text-sm text-amber-700">{traffic.throttleInfo}</p>
        </div>
      )}
    </div>
  );
}

/** 域名資訊區塊 */
function DomainSpecs({ cardData, t }: { cardData: CardData; t: Translations }) {
  return (
    <div className="p-4 space-y-3">
      <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">{t.domainInfo}</h3>
      
      <div className="space-y-2">
        <SpecCardFull icon={GlobeAltIcon} label={t.domainName} value={cardData.productName} />
        <SpecCardFull icon={BuildingOfficeIcon} label={t.registrar} value={cardData.provider} />
      </div>
    </div>
  );
}

/** 帳號資訊區塊 */
function AccountSpecs({ cardData, t }: { cardData: CardData; t: Translations }) {
  return (
    <div className="p-4 space-y-3">
      <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">{t.accountInfo}</h3>
      
      <div className="space-y-2">
        <SpecCardFull icon={UserIcon} label={t.platform} value={cardData.provider} />
        <SpecCardFull icon={CubeIcon} label={t.product} value={cardData.productName} />
      </div>
    </div>
  );
}

export function SpecsBlock({ cardData, t }: SpecsBlockProps) {
  const { category } = cardData;

  if (isServerType(category)) {
    return <ServerSpecs cardData={cardData} t={t} />;
  }

  if (category === 'domain') {
    return <DomainSpecs cardData={cardData} t={t} />;
  }

  if (category === 'account') {
    return <AccountSpecs cardData={cardData} t={t} />;
  }

  return null;
}

export { needsRouteType };
export default SpecsBlock;
